/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — catálogo de motivos
 * y documentación requerida por contingencia, investigado en el doc de Notion
 * "Rescates desde la plataforma" (Sprint 120, sección "Contingencias y
 * documentación requerida"). El motivo "Antigüedad (+10 años)" es el 7º
 * motivo propuesto en "Definimos MVP rescates" (pieza 2) — no depende de un
 * organismo externo, así que lleva menos documentación que el resto.
 *
 * Sin backend real: los documentos "subidos" solo se guardan en localStorage,
 * de navegador, para poder validar el flujo end-to-end en local.
 */

export interface RescueDoc {
	nameKey: string;
	descriptionKey: string;
}

export interface RescueReason {
	value: string;
	labelKey: string;
	// true si, tras rescatar por este motivo, ni el titular ni la empresa
	// pueden seguir aportando al plan (deja de haber relación laboral activa
	// o, en fallecimiento, deja de haber titular). Antigüedad y enfermedad
	// grave NO implican dejar de trabajar/aportar.
	stopsContributions: boolean;
	// false solo para los "supuestos excepcionales de liquidez" (enfermedad
	// grave y desempleo de larga duración, RD 304/2004 art. 9): por normativa
	// solo se pueden cobrar en forma de capital único, nunca como renta. El
	// resto de contingencias (antigüedad, jubilación, incapacidad, dependencia,
	// fallecimiento) permiten elegir capital, renta o mixto.
	allowsRenta: boolean;
}

// Actualizado (ago-2026): las 7 contingencias son seleccionables en
// cualquier desplegable de "solicitar/cambiar motivo" — antes se excluían
// Antigüedad y Enfermedad grave (stopsContributions: false) porque el caso
// híbrido (aportar y rescatar en paralelo) no estaba completo de punta a
// punta, pero esa distinción ya no aplica: todos los motivos funcionan
// igual, por solicitud manual. `stopsContributions` sigue existiendo (rige
// otras cosas, ver `reasonStopsContributions`), simplemente ya no filtra qué
// aparece en estos desplegables.
export const RESCUE_REASONS: RescueReason[] = [
	{
		value: 'seniority',
		labelKey: 'rescue.reasons.seniority',
		stopsContributions: false,
		allowsRenta: true
	},
	{
		value: 'retirement',
		labelKey: 'rescue.reasons.retirement',
		stopsContributions: true,
		allowsRenta: true
	},
	{
		value: 'disability',
		labelKey: 'rescue.reasons.disability',
		stopsContributions: true,
		allowsRenta: true
	},
	{
		value: 'dependency',
		labelKey: 'rescue.reasons.dependency',
		stopsContributions: true,
		allowsRenta: true
	},
	{
		value: 'severe-illness',
		labelKey: 'rescue.reasons.severeIllness',
		stopsContributions: false,
		allowsRenta: false
	},
	{
		value: 'unemployment',
		labelKey: 'rescue.reasons.unemployment',
		stopsContributions: true,
		allowsRenta: false
	},
	{
		value: 'death',
		labelKey: 'rescue.reasons.death',
		stopsContributions: true,
		allowsRenta: true
	}
];

const BASE_DOCS: RescueDoc[] = [
	{ nameKey: 'rescue.docs.dni.name', descriptionKey: 'rescue.docs.dni.description' },
	{
		nameKey: 'rescue.docs.bankCert.name',
		descriptionKey: 'rescue.docs.bankCert.description'
	},
	{ nameKey: 'rescue.docs.modelo145.name', descriptionKey: 'rescue.docs.modelo145.description' },
	{
		nameKey: 'rescue.docs.boletin.name',
		descriptionKey: 'rescue.docs.boletin.description'
	}
];

export const DOCS_BY_REASON: Record<string, RescueDoc[]> = {
	// Antigüedad: no depende de una resolución de un organismo externo, así
	// que no lleva el documento de resolución que sí exigen el resto.
	seniority: [...BASE_DOCS],
	retirement: [
		...BASE_DOCS,
		{
			nameKey: 'rescue.docs.retirementResolution.name',
			descriptionKey: 'rescue.docs.retirementResolution.description'
		}
	],
	disability: [
		...BASE_DOCS,
		{
			nameKey: 'rescue.docs.disabilityResolution.name',
			descriptionKey: 'rescue.docs.disabilityResolution.description'
		}
	],
	dependency: [
		...BASE_DOCS,
		{
			nameKey: 'rescue.docs.dependencyResolution.name',
			descriptionKey: 'rescue.docs.dependencyResolution.description'
		}
	],
	'severe-illness': [
		...BASE_DOCS,
		{
			nameKey: 'rescue.docs.medicalCert.name',
			descriptionKey: 'rescue.docs.medicalCert.description'
		}
	],
	unemployment: [
		...BASE_DOCS,
		{
			nameKey: 'rescue.docs.sepeCert.name',
			descriptionKey: 'rescue.docs.sepeCert.description'
		}
	],
	death: [
		{
			nameKey: 'rescue.docs.beneficiaryDni.name',
			descriptionKey: 'rescue.docs.beneficiaryDni.description'
		},
		{
			nameKey: 'rescue.docs.beneficiaryBankCert.name',
			descriptionKey: 'rescue.docs.beneficiaryBankCert.description'
		},
		{
			nameKey: 'rescue.docs.beneficiaryModelo145.name',
			descriptionKey: 'rescue.docs.beneficiaryModelo145.description'
		},
		{
			nameKey: 'rescue.docs.boletin.name',
			descriptionKey: 'rescue.docs.boletin.description'
		},
		{ nameKey: 'rescue.docs.deathCert.name', descriptionKey: 'rescue.docs.deathCert.description' },
		{
			nameKey: 'rescue.docs.beneficiaryProof.name',
			descriptionKey: 'rescue.docs.beneficiaryProof.description'
		},
		{ nameKey: 'rescue.docs.familyBook.name', descriptionKey: 'rescue.docs.familyBook.description' }
	]
};

export type RescueRequestStatus = 'pending' | 'confirmed';

export interface SavedRescueRequest {
	reason: string;
	requestedAt: string;
	// 'pending' al guardar la solicitud; pasa a 'confirmed' cuando el trámite
	// se completa. Sin backend real que procese rescates, hoy nada dispara
	// esa transición automáticamente — `confirmRescueRequest` queda lista para
	// cuando exista esa integración (ver Notion).
	status: RescueRequestStatus;
}

const storageKey = (email: string) => `rescate_solicitud_${email}`;

export function saveRescueRequest(email: string, reason: string): void {
	try {
		const payload: SavedRescueRequest = {
			reason,
			requestedAt: new Date().toISOString(),
			status: 'pending'
		};
		localStorage.setItem(storageKey(email), JSON.stringify(payload));
	} catch {
		// Scaffold local — si localStorage no está disponible, simplemente no persiste.
	}
}

export function loadRescueRequest(email: string): SavedRescueRequest | null {
	try {
		const raw = localStorage.getItem(storageKey(email));
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function confirmRescueRequest(email: string): void {
	try {
		const current = loadRescueRequest(email);
		if (!current) return;
		const payload: SavedRescueRequest = { ...current, status: 'confirmed' };
		localStorage.setItem(storageKey(email), JSON.stringify(payload));
	} catch {
		// Scaffold local — si localStorage no está disponible, simplemente no persiste.
	}
}

// UX SCAFFOLD — tras rescatar por un motivo que implica dejar de aportar
// (jubilación, incapacidad, dependencia, desempleo de larga duración,
// fallecimiento), la experiencia de esa persona debe pivotar a una vista
// centrada en el rescate (igual que María, ver `isRetiredBeneficiary` en
// MiBeneficioV2/ConfigurarBeneficioV2/PlanEmpleoV2/SimulatorsV2), no solo
// mostrar un badge. Antigüedad y enfermedad grave NO disparan esto: se puede
// seguir trabajando/aportando después de rescatar por esos dos motivos.
export function reasonStopsContributions(reason: string | null | undefined): boolean {
	if (!reason) return false;
	return RESCUE_REASONS.find((r) => r.value === reason)?.stopsContributions ?? false;
}
